import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  routeNames,
  routeParamNames,
} from '../../../assets/val/route-constants';
import { LoggingService } from 'src/app/service/logging-service/logging.service';
import { QueryParamsService } from 'src/app/service/query-params/query-params.service';
import * as ListSliderComponent from 'web-component-library/projects/component-library/src/lib/list-slider/list-slider.component';
import {
  CalculatorError,
  CalculatorOffer,
  CalculatorTexts,
} from '@app/model/calculator/calculator-data.dto';
import { Market } from '@app/model/payment-request/payment-request.dto';
import { CurrencyCode } from '@app/model/currency';
import calculatorTranslations from './loan-calculator.component-translations.json';
import { ValuePipe } from '../../shared/pipe/value.pipe';
import { LoanCalculatorService } from '@app/service/loan-calculator/loan-calculator.service';
import { RecordingService } from '@app/service/recording-service/recording.service';

@Component({
  selector: 'app-loan-calculator',
  styleUrls: ['./loan-calculator.component.css', '../page-shared.css'],
  templateUrl: './loan-calculator.component.html',
})
export class LoanCalculatorComponent implements OnInit {
  loading: boolean = true;

  primaryColor: string = '#DB305E';
  merchantId: string = '';
  selectedSliderInputValue: number;
  calculatorDataOffers: CalculatorOffer[];

  // Default to english and Euros
  locale: string = 'en';
  currencyCode: CurrencyCode = CurrencyCode.EUR;

  // Values
  amount: number;
  termsUrl: string;
  market: Market = null;
  effectiveInterest: string;
  nominalInterest: string;
  totalInterestFee: string;
  adminFee: string;
  initialFee: string;
  totalAmount: string;
  currentLoanTermsStr: string = '';

  // Text & translation
  translations: CalculatorTexts;

  sliderListOptions: ListSliderComponent.ListOption[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private queryParamsService: QueryParamsService,
    private valuePipe: ValuePipe,
    private loanCalculatorService: LoanCalculatorService,
    private loggingService: LoggingService,
    private recordingService: RecordingService
  ) {}

  async ngOnInit(): Promise<void> {
    // Set flag to not start recording if loan calculator
    this.recordingService.setIsLoanCalculator(true);
    this.recordingService.stopRecording();

    // Get calculator data
    this.getQueryData();
    this.translations = calculatorTranslations[this.locale];

    // Get loan calculator offers from QR-code
    this.getCalculateLoanOffersFromQr();
  }

  getCalculateLoanOffersFromQr(): void {
    const routeQrcode = this.route.snapshot.paramMap.get(
      routeParamNames.QR_CODE
    );

    this.loanCalculatorService
      .getLoanOffersByQrCodeToken(this.amount, routeQrcode)
      .subscribe({
        next: (loanOfferResponse) => {
          if (loanOfferResponse.error) {
            let errorMessage: string = this.translations.error;

            if (
              loanOfferResponse.error === CalculatorError.NO_OFFER_AVAILABLE
            ) {
              errorMessage = this.translations.offerError;
            }

            this.navigateToErrorPage(
              'Could not display loan calculator due to backend error: ' +
                loanOfferResponse.error,
              errorMessage,
              this.translations.infoMessage
            );
            return;
          }

          this.calculatorDataOffers = loanOfferResponse.availableOffers;
          this.termsUrl = loanOfferResponse.termsUrl;
          this.currencyCode = loanOfferResponse.currencyCode;

          this.sliderListOptions =
            this.mapCalculatorDataOffersToSliderListOptions(
              this.calculatorDataOffers
            );
          this.selectedSliderInputValue = this.getClosestListOption();

          this.updateValues(
            this.calculatorDataOffers[this.selectedSliderInputValue]
          );

          this.currentLoanTermsStr = this.buildLoanTermsString(
            this.calculatorDataOffers[this.selectedSliderInputValue],
            this.locale
          );

          this.loading = false;
        },
        error: (err) => {
          this.navigateToErrorPage(
            err,
            this.translations.error,
            this.translations.infoMessage
          );
        },
      });
  }

  navigateToErrorPage(
    logMessage: string,
    errorMessage: string,
    infoMessage: string
  ): void {
    this.loggingService.reportCrash(logMessage, null);
    this.router.navigate([routeNames.ERROR], {
      queryParams: {
        error: errorMessage,
        info: infoMessage,
        isFromLoanCalculator: 'true',
      },
    });
  }

  getFormattedInterest(interest: number): string {
    if (!interest) {
      return '0';
    }

    return interest.toFixed(2);
  }

  sliderValueChanged() {
    this.updateValues(this.calculatorDataOffers[this.selectedSliderInputValue]);
  }

  updateValues(selectedInstallmentOffer: CalculatorOffer): void {
    this.effectiveInterest = this.getFormattedInterest(
      selectedInstallmentOffer.effectiveInterest
        ? selectedInstallmentOffer.effectiveInterest * 100
        : 0
    );
    this.nominalInterest = this.getFormattedInterest(
      selectedInstallmentOffer.nominalInterest
    );
    this.totalInterestFee = this.getValuePipeTransformedValue(
      selectedInstallmentOffer.totalAmount -
        (selectedInstallmentOffer.initialAmount +
          selectedInstallmentOffer.monthlyFee *
            selectedInstallmentOffer.numberOfMonths +
          selectedInstallmentOffer.initialFee)
    );
    this.adminFee =
      this.currencyCode !== CurrencyCode.EUR
        ? this.getValuePipeTransformedValue(
            selectedInstallmentOffer.monthlyFee *
              selectedInstallmentOffer.numberOfMonths
          )
        : ''; // Not visable for germany
    this.initialFee =
      this.currencyCode !== CurrencyCode.EUR
        ? this.getValuePipeTransformedValue(selectedInstallmentOffer.initialFee)
        : ''; // Not visable for germany
    this.totalAmount = this.getValuePipeTransformedValue(
      selectedInstallmentOffer.totalAmount
    );

    this.currentLoanTermsStr = this.buildLoanTermsString(
      selectedInstallmentOffer,
      this.locale
    );
  }

  getClosestListOption(): number {
    return Math.floor(this.sliderListOptions.length / 2);
  }

  getLocaleFromRoute(): void {
    const routeLocale = this.route.snapshot.paramMap.get(
      routeParamNames.LOCALE
    );

    if (routeLocale) {
      const foundMarket: Market = Market[routeLocale.toUpperCase()];

      if (foundMarket) {
        this.locale = foundMarket.toLowerCase();
        return;
      }
    }

    // Default to english if unknown market.
    this.locale = 'en';
  }

  mapCalculatorDataOffersToSliderListOptions(
    calculatorDataOffers: CalculatorOffer[]
  ): ListSliderComponent.ListOption[] {
    let newData: ListSliderComponent.ListOption[];

    newData = calculatorDataOffers.map((installmentOffer, index) => {
      return {
        label: `${this.getValuePipeTransformedValue(
          installmentOffer.monthlyCost
        )} / ${installmentOffer.numberOfMonths} ${this.translations.month}`,
        value: index,
      };
    });

    return newData;
  }

  getValuePipeTransformedValue(value: number): string {
    return `${this.valuePipe.transform(
      {
        amount: value,
        currencyCode: this.currencyCode,
      },
      true
    )}`;
  }

  getQueryData(): void {
    this.amount = this.getAmountNumberFromQueryParam(
      this.queryParamsService.getQueryParams().amount
    );

    if (!this.amount || isNaN(this.amount)) {
      this.navigateToErrorPage(
        'No valid amount selected for calculation, found value: ' +
          this.queryParamsService.getQueryParams().amount,
        calculatorTranslations[this.locale].error,
        this.translations.infoMessage
      );
      return;
    }

    this.getLocaleFromRoute();

    const overridePrimaryColor = this.queryParamsService.getQueryParams().color;
    if (overridePrimaryColor) {
      this.primaryColor = `#${overridePrimaryColor}`;
    }
  }

  getAmountNumberFromQueryParam(amountString: string): number {
    const formattedAmountString = amountString
      .replace(',', '.')
      .replace('%2c', '.');

    return Number(formattedAmountString);
  }

  buildLoanTermsString(
    selectedLoanOption: CalculatorOffer,
    language: string
  ): string {
    // TODO: Add rest of languages (En, De, At).
    if (language === 'se') {
      return `Valt kreditbelopp ${selectedLoanOption.initialAmount.toLocaleString(
        language
      )} ${this.currencyCode}, delbetalning ${
        selectedLoanOption.numberOfMonths
      } mån. Totala summa av betalningar är ${selectedLoanOption.totalAmount.toLocaleString(
        language
      )} ${
        this.currencyCode
      } och den effektiva räntan är ${this.getFormattedInterest(
        selectedLoanOption.effectiveInterest * 100
      )}%. Rörlig årsränta ${this.getFormattedInterest(
        selectedLoanOption.nominalInterest
      )}%, uppläggningsavgift ${selectedLoanOption.initialFee.toLocaleString(
        language
      )} ${
        this.currencyCode
      }, administrativ avgift ${selectedLoanOption.monthlyFee.toLocaleString(
        language
      )} ${
        this.currencyCode
      }/mån och månadsbelopp ${selectedLoanOption.monthlyCost.toLocaleString(
        language
      )} ${
        this.currencyCode
      } totala summan av betalningar ${selectedLoanOption.totalAmount.toLocaleString(
        language
      )} ${this.currencyCode}.`;
    } else if (language === 'no') {
      return `Valgt kredittbeløp ${selectedLoanOption.initialAmount.toLocaleString(
        language
      )} ${this.currencyCode}, delbetaling ${
        selectedLoanOption.numberOfMonths
      } måneder. Den effektive renten er ${this.getFormattedInterest(
        selectedLoanOption.effectiveInterest * 100
      )}%. Variabel årlig rente ${this.getFormattedInterest(
        selectedLoanOption.nominalInterest
      )}%, etableringsgebyr ${selectedLoanOption.initialFee.toLocaleString(
        language
      )} ${
        this.currencyCode
      }, administrasjonsgebyr ${selectedLoanOption.monthlyFee.toLocaleString(
        language
      )} ${
        this.currencyCode
      }/mnd og månedlig beløp ${selectedLoanOption.monthlyCost.toLocaleString(
        language
      )} ${
        this.currencyCode
      }. Total sum av betalinger ${selectedLoanOption.totalAmount.toLocaleString(
        language
      )} ${this.currencyCode}.`;
    }

    return '';
  }
}
